import SharedDialog from '../../../../../global/Modal/Modal';
import html2pdf from 'html2pdf.js';
import { useRef, useState, useEffect } from 'react';
import ChatBot from '@/components/ChatBot/ChatBot';
import SharedButton from '../../../../../global/Button/Button';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

export default function EvidenceSummaryModal({ open = false, onClose, content, data }) {
  const summaryRef = useRef(null);
  const [openChatBot, setOpenChatBot] = useState(false);
  const [chatKey, setChatKey] = useState(0);

  // Reset chatbot state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setOpenChatBot(false);
    }
  }, [open]);

  const handleOpenChatbot = () => {
    setOpenChatBot(true);
    // Force remount of ChatBot to trigger initial message
    setChatKey(prev => prev + 1);
  };

  const downloadSummary = () => {
    if (!content || !summaryRef.current) return;
    const summaryEl = summaryRef.current as HTMLElement;
    const clone = summaryEl.cloneNode(true) as HTMLElement;

    const button = clone.querySelector('.ask-ai-button');
    if (button) button.remove();

    const opt = {
      margin: [20, 40, 40, 20] as [number, number, number, number],
      filename: 'evidence-summary.pdf',
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'pt', format: 'a4', orientation: 'portrait' as const },
    };

    html2pdf().set(opt).from(clone).save();
  };

  // Prepare the context message for the chatbot
  const contextMessage = content
    ? `I have the following evidence summary:\n\n${content.replace(/<[^>]*>/g, '')}\n\nPlease confirm you understand this context and let me know how you can help me analyze it.`
    : '';

  return (
    <SharedDialog
      isOpen={open}
      onClose={onClose}
      cancelText="Close"
      hasConfirmBtn={content && !openChatBot}
      onConfirm={downloadSummary}
      confirmText="Download Summary"
      size="xl"
    >
      <div ref={summaryRef}>
        <div className="row">
          {openChatBot && (
            <button onClick={() => setOpenChatBot(false)} className="mb-4">
              <ArrowLeftIcon className="h-6 w-6 inline mr-2" />
              <span>Back to summary</span>
            </button>
          )}
          {!openChatBot && <h6 className="font-bold text-2xl mb-4">Evidence Summary</h6>}
        </div>
        {!openChatBot ? (
          <>
            {!content && <p>There was a problem trying to get the summary. Please try again.</p>}
            <p dangerouslySetInnerHTML={{ __html: content }} style={{ whiteSpace: 'break-spaces' }} />

            {content && data && data.length > 0 && (
              <div className="mt-6 flex justify-center ask-ai-button">
                <SharedButton
                  size="md"
                  className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold px-6 py-3"
                  onClick={handleOpenChatbot}
                >
                  <i className="bi bi-chat-dots mr-2"></i>
                  Ask AI about this summary
                </SharedButton>
              </div>
            )}
          </>
        ) : (
          <ChatBot
            key={chatKey}
            chatId={`evidence-summary-${chatKey}`}
            question={contextMessage}
            shortQuestion="Evidence Summary Context"
            autoSend={true}
            style={{ height: '80px' }}
          />
        )}
      </div>
    </SharedDialog>
  );
}
